import lity from 'lity';

export class Carousel {
    PREFIX = 'image-carousel';
    PREFIX_NAVIGATION = 'carousel';
    _activeDot = null;
    _dots = [];

    get activeTitle () {
        return this._activeTitle;
    }

    get nbSlides () {
        return Array.from(this.slides).filter(slide => slide.getAttribute('aria-hidden') === null).length;
    }

    get nbTotalSlides () {
        return Array.from(this.slides).length;
    }

    get dotNavigation () {
        return this.domElm.querySelector(`.${this.PREFIX_NAVIGATION}__dot-navigation`);
    }

    set activeTitle (value) {
        this._activeTitle?.classList.remove('active');
        value.classList.add('active');
        this._activeTitle = value;
    }

    set setDefaultFirst (value) {
        this._setDefaultFirst = value;
    }

    get setDefaultFirst () {
        return this._setDefaultFirst;
    }

    _setDefaultFirst = true;
    _activeTitle = null;

    get scrollableContainer () {
        return this._scrollableContainer;
    }

    set scrollableContainer (value) {
        this._scrollableContainer = value;
    }

    AUTOPLAY_INTERVAL = 5000;

    get isAutomation () {
        return this._isAutomation ?? false;
    }

    set isAutomation (value) {
        this._isAutomation = value;
    }

    get slides () {
        return this.scrollableContainer.children;
    }

    get domElm () {
        return this._domElm;
    }

    set domElm (value) {
        this._domElm = value;
    }

    get activeElement () {
        return this._activeElement;
    }

    get autoplayTimer () {
        return this._autoplayTimer;
    }

    get navigation () {
        return this._navigation;
    }

    set navigation (value) {
        this._navigation = value;
    }

    get prevBtn () {
        return this._prevBtn;
    }

    set prevBtn (value) {
        this._prevBtn = value;
    }

    get nextBtn () {
        return this._nextBtn;
    }

    set nextBtn (value) {
        this._nextBtn = value;
    }

    set autoplayTimer (value) {
        this._autoplayTimer = value;
    }

    set activeElement (value) {
        // Avoir null values
        if (value === null) return;

        this._activeElement?.classList.remove('active');
        value?.classList.add('active');

        this._activeElement = value;

        if (this.hasTitles) {
            this.activeTitle = this.domElm.querySelector('[data-linked-id=\'' + this.activeElement.dataset.id + '\']');
        }

        requestAnimationFrame(() => this.animateChangement());

        if (this.dotNavigation)
            this.activeDot = this.dotNavigation.querySelector('[data-linked-id=\'' + this.activeElement.dataset.elmId + '\']');
    }

    /**
     * @param {Element} domElm
     */
    constructor (domElm) {
        this.domElm = domElm;
        this.scrollableContainer = this.domElm.querySelector('.carousel__slides') ?? this.domElm;
        this.init();
    }

    set activeDot (value) {
        if (value === null) return;

        this._activeDot?.classList.remove('active');
        value?.classList.add('active');

        this._activeDot = value;
    }

    get hasTitles () {
        return this.domElm.classList.contains('carousel--nav-arrow-title');
    }

    /**
     * @return {number}
     */
    get slidePerPage () {
        return parseInt(getComputedStyle(this.domElm).getPropertyValue(this.domElm.dataset.variableForColumn) ?? 1);
    }

    init () {
        this.addListeners();
        this.addNavigation();

        if (this.domElm.dataset.autoplay !== undefined) {
            this.addAutoscroll();
        }

        if (this.setDefaultFirst) {
            this.activeElement = this.slides[0];
        }

        this.handleLightbox();

        this.domElm.classList.add('initialized');
    }

    handleLightbox () {

        const lightboxImage = this.domElm.querySelectorAll('[data-has-lightbox] picture img');
        const lightboxSvg = this.domElm.querySelectorAll('[data-has-lightbox] [data-lightbox-url]');

        lightboxImage.forEach(image => {
            image.addEventListener('click', (e) => {
                const currentSrc = image.currentSrc || image.src;
                lity(currentSrc);
            });
        });

        lightboxSvg.forEach(image => {
            image.addEventListener('click', (e) => {
                lity(image.getAttribute('data-lightbox-url'));
            });
        });
    }

    addListeners () {
        this.scrollableContainer.addEventListener('scroll', () => {
            if (!this.isAutomation) {
                this.detectFirst();
            }

            this.updateNavigation();

            if (this.domElm.dataset.autoplay !== undefined) {
                this.resetAutoplay();
            }
        });

        window.addEventListener('resize', e => {
            if (this.domElm.dataset.navigation.includes('dot')) {
                this.generateDots();
            }
        });
    }

    addNavigation () {
        if (this.domElm.dataset.navigation.includes('arrow') || this.domElm.dataset.navigation === 'arrow-title') {
            this.navigation = this.domElm.querySelector('.carousel__navigation');
            this.prevBtn = this.navigation.querySelector('.carousel__navigation-prev');
            this.nextBtn = this.navigation.querySelector('.carousel__navigation-next');

            this.prevBtn?.addEventListener('click', () => {
                this.prev();
            });

            this.nextBtn?.addEventListener('click', () => {
                this.next();
            });
        }

        if (this.domElm.dataset.navigation.includes('dot')) {
            this.generateDots();
        }
    }

    addAutoscroll () {
        this.startAutoplay();
    }

    startAutoplay () {
        this.autoplayTimer = setInterval(() => {
            this.isAutomation = true;
            this.next();
        }, this.AUTOPLAY_INTERVAL);
    }

    resetAutoplay () {
        // Si un intervalle existe, on l'efface
        if (this.autoplayTimer) {
            clearInterval(this.autoplayTimer);
        }

        // Démarrer un nouvel intervalle
        this.startAutoplay();
    }

    next () {
        this.isAutomation = true;
        if (this.checkMaxScrollReached()) {
            this.activeElement = this.scrollableContainer.firstElementChild;
        } else {
            this.activeElement = this.activeElement.nextElementSibling ?? this.scrollableContainer.firstElementChild;
        }

        setTimeout(() => {
            this.isAutomation = false;
        }, 500);
    }

    updateNavigation () {
        if (this.nextBtn) this.nextBtn.disabled = !this.isNextAvailable();

        if (this.prevBtn) this.prevBtn.disabled = !this.isPreviousAvailable();
    }

    isPreviousAvailable () {
        return this.scrollableContainer.scrollLeft > 0;
    }

    isNextAvailable () {
        return !this.checkMaxScrollReached();
    }

    prev () {
        this.isAutomation = true;

        this.activeElement = this.activeElement.previousElementSibling ?? this.scrollableContainer.firstElementChild;

        setTimeout(() => {
            this.isAutomation = false;
        }, 500);
    }

    animateChangement () {
        const container = this.activeElement.parentElement;

        const elementRect = this.activeElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        const computedStyles = getComputedStyle(container);

        const scrollPaddingLeft = this.parseCSSLength(computedStyles.scrollPaddingLeft);

        const scrollTarget = container.scrollLeft + (elementRect.left - containerRect.left) - scrollPaddingLeft;

        container.scrollTo({
            left: scrollTarget, behavior: 'smooth'
        });
    }

    parseCSSLength (value) {
        if (!value || value === 'auto') return 0;
        return parseFloat(value) || 0;
    }

    checkMaxScrollReached () {
        const scrollWidth = this.activeElement.parentElement.scrollWidth - this.activeElement.parentElement.clientWidth;
        const clientWidth = this.activeElement.parentElement.scrollLeft;

        return scrollWidth - clientWidth < 1;
    }

    detectFirst () {
        const threshold = 30;
        const parent = this.slides[0].parentElement;
        const rawScrollPaddingLeft = getComputedStyle(parent).scrollPaddingLeft;
        const scrollPaddingLeft = parseInt(rawScrollPaddingLeft === 'auto' ? 0 : rawScrollPaddingLeft || 0, 10);

        let smallest = { elm: this.slides[0], dist: null };

        for (let i = 0; i < this.slides.length; i++) {
            const slide = this.slides[i];
            const dist = slide.offsetLeft - parent.scrollLeft - scrollPaddingLeft + threshold;

            if (dist >= 0 && (dist < smallest.dist || smallest.dist === null)) {
                smallest.elm = slide;
                smallest.dist = dist;
            }
        }

        // Handle discrete the active element changing when scrolling
        this._activeElement?.classList.remove('active');
        this._activeElement = smallest.elm;
        this._activeElement?.classList.add('active');

        // Set new active dot
        if (this.dotNavigation)
            this.activeDot = this.dotNavigation.querySelector('[data-linked-id=\'' + smallest.elm.dataset.elmId + '\']');
    }

    generateDots () {
        if (this._dots.length === 0) {
            for (let i = 0; i < this.nbSlides; i++) {
                const slide = this.slides[i];
                const dot = document.createElement('div');
                dot.classList.add('dot');
                dot.setAttribute('data-linked-id', slide.dataset.elmId);
                this.dotNavigation.prepend(dot);
                this._dots.push(dot);
            }
        }

        for (let i = 0; i < this._dots.length; i++) {
            if (i >= this._dots.length - (this.slidePerPage - 1)) {
                this._dots[i].style.display = 'none';
            } else {
                this._dots[i].style.display = 'block';
            }
        }
    }
}
